import { FC, useState, useMemo } from "react"
import { LightBtn } from "../buttons"
import {
  Plus,
  Trash2,
  AlertTriangle,
  ChevronDown,
  ChevronUp
} from "lucide-react"

type FieldType = "default" | "raw"

interface FieldRow {
  id: string
  type: FieldType
  key: string
  value: string
}

export interface GlobalDefaults {
  defaults?: Record<string, any>
  raw_defaults?: Record<string, any>
}

interface Props {
  currentDefaults: GlobalDefaults
  onChange: (defaults: GlobalDefaults) => void
}

let rowIdCounter = 0

/**
 * Converts global defaults into a flat list of field rows
 */
const defaultsToRows = (globalDefaults: GlobalDefaults): FieldRow[] => {
  const rows: FieldRow[] = []

  if (globalDefaults.defaults) {
    Object.entries(globalDefaults.defaults).forEach(([key, value]) => {
      rows.push({
        id: `row-${++rowIdCounter}`,
        type: "default",
        key,
        value: String(value)
      })
    })
  }

  if (globalDefaults.raw_defaults) {
    // Filter out loanInformation which is handled by CustomFieldsManager
    Object.entries(globalDefaults.raw_defaults)
      .filter(([key]) => key !== "loanInformation")
      .forEach(([key, value]) => {
        rows.push({
          id: `row-${++rowIdCounter}`,
          type: "raw",
          key,
          value: String(value)
        })
      })
  }

  return rows
}

/**
 * Converts field rows back into global defaults structure
 * Preserves loanInformation from raw_defaults (OB custom fields)
 */
const rowsToDefaults = (
  rows: FieldRow[],
  existingRawDefaults?: Record<string, any>
): GlobalDefaults => {
  const defaults: Record<string, any> = {}
  const raw_defaults: Record<string, any> = {}

  // Preserve existing loanInformation (OB custom fields)
  if (existingRawDefaults?.loanInformation) {
    raw_defaults.loanInformation = existingRawDefaults.loanInformation
  }

  rows.forEach(row => {
    if (!row.key.trim()) return

    // Auto-detect numbers
    const value = row.value.trim()
    const numValue = Number(value)
    const finalValue =
      value !== "" && !isNaN(numValue) && isFinite(numValue) ? numValue : value

    if (row.type === "default") {
      defaults[row.key.trim()] = finalValue
    } else {
      raw_defaults[row.key.trim()] = finalValue
    }
  })

  return { defaults, raw_defaults }
}

const GlobalDefaultsEditor: FC<Props> = ({ currentDefaults, onChange }) => {
  const [rows, setRows] = useState<FieldRow[]>(() =>
    defaultsToRows(currentDefaults)
  )
  const [newFieldType, setNewFieldType] = useState<FieldType>("default")
  const [newFieldKey, setNewFieldKey] = useState("")
  const [newFieldValue, setNewFieldValue] = useState("")
  const [isExpanded, setIsExpanded] = useState(() => {
    // Auto-expand if there are existing fields
    const existingRows = defaultsToRows(currentDefaults)
    return existingRows.length > 0
  })

  // Check for duplicate keys within the same type
  const isDuplicateKey = useMemo(() => {
    if (!newFieldKey.trim()) return false
    return rows.some(
      r =>
        r.type === newFieldType &&
        r.key.toLowerCase() === newFieldKey.trim().toLowerCase()
    )
  }, [rows, newFieldType, newFieldKey])

  const updateAndNotify = (updatedRows: FieldRow[]) => {
    setRows(updatedRows)
    onChange(rowsToDefaults(updatedRows, currentDefaults.raw_defaults))
  }

  const handleAddField = () => {
    const trimmedKey = newFieldKey.trim()
    if (!trimmedKey || isDuplicateKey) return

    const newRow: FieldRow = {
      id: `row-${++rowIdCounter}`,
      type: newFieldType,
      key: trimmedKey,
      value: newFieldValue.trim()
    }

    updateAndNotify([...rows, newRow])
    setNewFieldKey("")
    setNewFieldValue("")
  }

  const handleUpdateRow = (
    id: string,
    field: "type" | "key" | "value",
    value: string
  ) => {
    updateAndNotify(
      rows.map(row => (row.id === id ? { ...row, [field]: value } : row))
    )
  }

  const handleDeleteRow = (id: string) => {
    updateAndNotify(rows.filter(row => row.id !== id))
  }

  const inputClassName =
    "block w-full px-3 py-2 border border-neutral-200 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"

  const typeToggleClassName = (isActive: boolean, isRaw: boolean = false) =>
    `px-3 py-2 text-xs font-medium transition-colors ${
      isActive
        ? isRaw
          ? "bg-amber-500 text-white"
          : "bg-primary-600 text-white"
        : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
    }`

  return (
    <div className="border border-neutral-200 rounded-lg bg-white">
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-50 transition-colors rounded-lg"
      >
        <div>
          <h4 className="font-medium text-neutral-900">
            Global Request Defaults
          </h4>
          <p className="text-xs text-neutral-500 mt-0.5">
            Default values applied to all pricing requests for this brand
            {rows.length > 0 && (
              <span className="ml-2 text-primary-600">
                ({rows.length} configured)
              </span>
            )}
          </p>
        </div>
        {isExpanded ? (
          <ChevronUp size={20} className="text-neutral-400" />
        ) : (
          <ChevronDown size={20} className="text-neutral-400" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-neutral-200 p-4 space-y-4">
          {/* Warning Banner */}
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle
              size={18}
              className="text-amber-600 shrink-0 mt-0.5"
            />
            <div className="text-sm text-amber-800">
              <span className="font-medium">Advanced Feature:</span> These
              defaults apply to all loan officers in this brand. LO-specific
              defaults will override these values. Incorrect values can cause
              pricing errors.
            </div>
          </div>

          {/* Field Type Legend */}
          <div className="flex gap-4 text-xs text-neutral-500">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-primary-500"></span>
              <span>
                BB Default – Standard request fields (e.g., lock_period)
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              <span>Engine Raw – Engine-specific fields with dot notation</span>
            </div>
          </div>

          {/* Existing Fields */}
          {rows.length > 0 && (
            <div className="space-y-2">
              {rows.map(row => (
                <div
                  key={row.id}
                  className={`flex gap-2 items-center p-2 rounded-md border ${
                    row.type === "default"
                      ? "border-primary-100 bg-primary-50/30"
                      : "border-amber-100 bg-amber-50/30"
                  }`}
                >
                  {/* Type Badge (read-only) */}
                  <span
                    className={`px-2.5 py-1.5 text-xs font-medium rounded-md shrink-0 ${
                      row.type === "default"
                        ? "bg-primary-100 text-primary-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {row.type === "default" ? "BB" : "Raw"}
                  </span>

                  {/* Key Input */}
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      value={row.key}
                      onChange={e =>
                        handleUpdateRow(row.id, "key", e.target.value)
                      }
                      placeholder="Field key"
                      className={inputClassName}
                    />
                  </div>

                  {/* Value Input */}
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      value={row.value}
                      onChange={e =>
                        handleUpdateRow(row.id, "value", e.target.value)
                      }
                      placeholder="Value"
                      className={inputClassName}
                    />
                  </div>

                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={() => handleDeleteRow(row.id)}
                    className="p-2 shrink-0 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {rows.length === 0 && (
            <div className="text-neutral-500 text-center py-6 bg-neutral-50 rounded-lg border border-dashed border-neutral-200">
              No global defaults configured. Add defaults that will apply to all
              LOs.
            </div>
          )}

          {/* Add New Field */}
          <div className="border-t border-neutral-200 pt-4">
            <h5 className="text-sm font-medium text-neutral-700 mb-3">
              Add New Global Default
            </h5>
            <div className="flex gap-2 items-end">
              {/* Type Toggle */}
              <div className="shrink-0">
                <label className="block text-xs font-medium text-neutral-600 mb-1">
                  Type
                </label>
                <div className="flex rounded-md overflow-hidden border border-neutral-200">
                  <button
                    type="button"
                    onClick={() => setNewFieldType("default")}
                    className={typeToggleClassName(
                      newFieldType === "default",
                      false
                    )}
                  >
                    BB Default
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewFieldType("raw")}
                    className={typeToggleClassName(
                      newFieldType === "raw",
                      true
                    )}
                  >
                    Engine Raw
                  </button>
                </div>
              </div>

              {/* Key Input */}
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-medium text-neutral-600 mb-1">
                  Key
                </label>
                <input
                  type="text"
                  value={newFieldKey}
                  onChange={e => setNewFieldKey(e.target.value)}
                  placeholder={
                    newFieldType === "default"
                      ? "e.g. lock_period"
                      : "e.g. criteria.channel"
                  }
                  className={inputClassName}
                />
              </div>

              {/* Value Input */}
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-medium text-neutral-600 mb-1">
                  Value
                </label>
                <input
                  type="text"
                  value={newFieldValue}
                  onChange={e => setNewFieldValue(e.target.value)}
                  placeholder="e.g. 45"
                  className={inputClassName}
                />
              </div>

              {/* Add Button */}
              <LightBtn
                type="button"
                onClick={handleAddField}
                disabled={!newFieldKey.trim() || isDuplicateKey}
                className="flex items-center shrink-0"
              >
                <Plus size={14} className="mr-1" />
                Add
              </LightBtn>
            </div>

            {isDuplicateKey && newFieldKey.trim() && (
              <p className="text-xs text-red-600 mt-2">
                A {newFieldType === "default" ? "BB default" : "raw"} field with
                this key already exists.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default GlobalDefaultsEditor
